// apps/frontend/src/hooks/usePendingAdminNotices.ts
// Poll /api/v1/notices/pending pour alimenter le modal de notice admin.
// Refresh au mount + toutes les 60s — assez pour catcher une notice
// qu'un admin viendrait d'envoyer pendant que l'user est sur l'app.

import { useCallback, useEffect, useState } from 'react';
import apiClient from '../services/apiClient';

export type AdminNoticeSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export interface AdminNotice {
  id: string;
  userId: string;
  senderAdminId: string;
  severity: AdminNoticeSeverity;
  title: string;
  body: string;
  actionUrl: string | null;
  actionLabel: string | null;
  seenAt: string | null;
  createdAt: string;
}

const REFRESH_INTERVAL_MS = 60_000;

export function usePendingAdminNotices(enabled: boolean) {
  const [notices, setNotices] = useState<AdminNotice[]>([]);

  const fetchNotices = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await apiClient.request<{ notices: AdminNotice[] }>(
        '/api/v1/notices/pending'
      );
      if (res.success) {
        setNotices((res.data as any)?.notices ?? []);
      }
    } catch {
      // silencieux
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setNotices([]);
      return;
    }
    fetchNotices();
    const id = setInterval(fetchNotices, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, fetchNotices]);

  // Marque localement la notice comme vue (optimistic) et appelle l'API.
  // L'API renvoie 200 meme si deja vue (idempotent).
  const acknowledge = useCallback(async (noticeId: string) => {
    setNotices(prev => prev.filter(n => n.id !== noticeId));
    try {
      await apiClient.request(`/api/v1/notices/${noticeId}/seen`, {
        method: 'POST',
      });
    } catch {
      // Si l'API echoue, le polling recharge — on retentera plus tard
    }
  }, []);

  return { notices, acknowledge };
}
